/**
 * tesseractShadowTouchPipeline — the 4D hypercube rotates toward
 * your cursor. The pointer position adds a slow rotation that
 * drifts toward the pointer's quadrant of the screen, so the
 * tesseract appears to "follow" the cursor in 4D. Click adds a
 * "breathe pulse" — the tesseract briefly expands and contracts
 * its inner cube.
 *
 * Based on tesseractShadowPipeline.ts — same edge structure,
 * same audio reactivity, but the 4D rotation is influenced by
 * the pointer.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface TesseractShadowTouchPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  uPointer: WebGLUniformLocation | null;
  uPointerActive: WebGLUniformLocation | null;
  uClickPos: WebGLUniformLocation | null;
  uClickAge: WebGLUniformLocation | null;
  bands: Float32Array;
  smoothX: number;
  smoothY: number;
  _active: number;
  clicks: { x: number; y: number; t: number }[];
}

const MAX_CLICKS = 2;
const CLICK_LIFETIME = 1.2;

const VERT_SRC = `#version 300 es
precision highp float; layout(location=0) in vec2 aPos; out vec2 vUV;
void main(){ vUV = aPos*0.5+0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

const FRAG_SRC = `#version 300 es
precision highp float; in vec2 vUV; out vec4 outColor;
uniform vec2 uResolution; uniform float uTime;
uniform vec3 uBass, uMid, uHigh;
// Pointer in canvas pixels (y-up, after the embed's y-flip).
uniform vec2 uPointer;
uniform float uPointerActive;
// Up to 2 active clicks.
uniform vec4 uClickPos;
uniform vec4 uClickAge;

// 4D rotation in the XW and YW planes.
vec4 rot4D(vec4 p, float a, float b) {
  float ca = cos(a), sa = sin(a);
  float cb = cos(b), sb = sin(b);
  vec4 q = vec4(
    ca * p.x + sa * p.w,
    cb * p.y + sb * p.w,
    p.z,
    -sa * p.x + ca * p.w
  );
  q = vec4(
    q.x,
    cb * q.y - sb * q.w,
    q.z,
    sb * q.y + cb * q.w
  );
  return q;
}

vec3 project4to3(vec4 p, float cameraDist) {
  float w = 1.0 / (cameraDist - p.w);
  return vec3(p.x * w, p.y * w, p.z * w);
}

vec4 tesseractVertex(int i) {
  return vec4(
    float((i & 1) * 2 - 1),
    float((i & 2) - 1),
    float((i & 4) / 2 - 1),
    float((i & 8) / 4 - 1)
  );
}

vec2 tesseractProj(int i, float a, float b, float yRot, float cameraDist) {
  vec4 v = tesseractVertex(i);
  vec4 vR = rot4D(v, a, b);
  // 3D Y rotation.
  float cy = cos(yRot), sy = sin(yRot);
  vec3 p3 = vec3(cy * vR.x + sy * vR.z, vR.y, -sy * vR.x + cy * vR.z);
  p3 = project4to3(vec4(p3, vR.w), cameraDist);
  // Orthographic 2D projection (drop z).
  return vec2(p3.x, p3.y);
}

bool tesseractEdge(int i, int j) {
  int diff = i ^ j;
  return (diff == 1 || diff == 2 || diff == 4 || diff == 8);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Pointer in shader-space (y-up — the forwarder already flipped).
  vec2 pPos = (uPointer - 0.5 * uResolution) / uResolution.y;
  // Map pointer to a rotation offset. The pointer's normalized
  // position drives an extra 2D rotation in the tesseract's
  // own coordinate system.
  float ptrRotX = pPos.x * 1.5;   // -1.5..+1.5 rad
  float ptrRotY = pPos.y * 1.5;
  // The pointer's effect ramps in smoothly with uPointerActive.
  float ptrInfluence = uPointerActive;

  // Click breathe pulse — add a temporary inflate/deflate.
  float breathe = 0.0;
  if (uClickPos.x >= 0.0 && uClickAge.x < 1.2) {
    // A cosine pulse that goes 0 → 1 → 0 over the lifetime.
    float a = uClickAge.x / 1.2;
    breathe += sin(a * 3.14159) * 1.0;
  }
  if (uClickPos.z >= 0.0 && uClickAge.y < 1.2) {
    float a = uClickAge.y / 1.2;
    breathe += sin(a * 3.14159) * 0.8;
  }

  float r = length(p);
  vec3 bg = mix(vec3(0.04, 0.06, 0.12), vec3(0.01, 0.01, 0.04), smoothstep(0.0, 0.7, r));
  float ax = abs(p.x);
  float ay = abs(p.y);
  if (ax < 0.001 && ay < 0.5) bg += vec3(0.05, 0.10, 0.15) * 0.3;
  if (ay < 0.001 && ax < 0.5) bg += vec3(0.05, 0.10, 0.15) * 0.3;
  vec3 col = bg;

  // Rotation angles — base + pointer-influenced.
  float a4 = uTime * (0.3 + bass * 0.4) + ptrRotX * ptrInfluence;
  float b4 = uTime * (0.2 + bass * 0.3) + 1.5 + ptrRotY * ptrInfluence;
  float yRot = uTime * 0.4 + ptrRotX * 0.5 * ptrInfluence;
  // The camera distance pulses with breathe.
  float cameraDist = 2.5 + 0.5 * sin(uTime * 0.3) + breathe * 0.4;

  float edgeThickness = 0.005 + 0.008 * mid;
  for (int i = 0; i < 16; i++) {
    for (int j = 0; j < 16; j++) {
      if (i < j && tesseractEdge(i, j)) {
        vec2 a = tesseractProj(i, a4, b4, yRot, cameraDist);
        vec2 b = tesseractProj(j, a4, b4, yRot, cameraDist);
        vec2 ab = b - a;
        float t = clamp(dot(p - a, ab) / max(dot(ab, ab), 0.0001), 0.0, 1.0);
        vec2 closest = a + ab * t;
        float d = length(p - closest);
        float lineMask = 1.0 - smoothstep(0.0, edgeThickness, d);
        int bit = i ^ j;
        vec3 edgeColor;
        if (bit == 1 || bit == 8) {
          edgeColor = vec3(0.95, 0.55, 0.85);
        } else if (bit == 2 || bit == 4) {
          edgeColor = vec3(0.55, 0.85, 0.95);
        } else {
          edgeColor = vec3(0.95, 0.95, 0.65);
        }
        col += edgeColor * lineMask * (0.7 + 0.5 * high);
      }
    }
  }

  // Pointer glow — a soft spot at the cursor's projected 2D position.
  // We don't have the 4D position of the pointer, so we use the
  // pointer's 2D screen position directly.
  float ptrGlow = exp(-length(p - pPos) * 12.0) * ptrInfluence * 0.4;
  col += vec3(0.7, 0.95, 1.0) * ptrGlow;

  // Click ring — a quick expanding ring at each click.
  // Rendered in 2D screen space at the click position.
  if (uClickPos.x >= 0.0) {
    vec2 c1 = (uClickPos.xy - 0.5 * uResolution) / uResolution.y;
    float r1 = uClickAge.x * 0.5;
    float ring1 = exp(-pow(length(p - c1) - r1, 2.0) * 200.0) * (1.0 - uClickAge.x / 1.2);
    col += vec3(1.0, 0.85, 1.0) * ring1 * 1.4;
  }
  if (uClickPos.z >= 0.0) {
    vec2 c2 = (uClickPos.zw - 0.5 * uResolution) / uResolution.y;
    float r2 = uClickAge.y * 0.5;
    float ring2 = exp(-pow(length(p - c2) - r2, 2.0) * 200.0) * (1.0 - uClickAge.y / 1.2);
    col += vec3(1.0, 0.85, 1.0) * ring2 * 1.4;
  }

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}`;

export function computeTesseractShadowTouchBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeTesseractShadowTouchRealtimeBands(
  freqData: Uint8Array, out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) { sum += freqData[k]; count++; }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createTesseractShadowTouchPipeline(gl: WebGL2RenderingContext): TesseractShadowTouchPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution:    gl.getUniformLocation(program, "uResolution"),
    uTime:          gl.getUniformLocation(program, "uTime"),
    uBass:          gl.getUniformLocation(program, "uBass"),
    uMid:           gl.getUniformLocation(program, "uMid"),
    uHigh:          gl.getUniformLocation(program, "uHigh"),
    uPointer:       gl.getUniformLocation(program, "uPointer"),
    uPointerActive: gl.getUniformLocation(program, "uPointerActive"),
    uClickPos:      gl.getUniformLocation(program, "uClickPos"),
    uClickAge:      gl.getUniformLocation(program, "uClickAge"),
    bands: new Float32Array(9),
    smoothX: 0, smoothY: 0,
    _active: 0,
    clicks: [],
  };
}

export function drawTesseractShadowTouch(
  gl: WebGL2RenderingContext,
  pipe: TesseractShadowTouchPipeline,
  width: number, height: number, t: number,
): void {
  // Click uniforms from the ring buffer.
  const cp = [0, 0, 0, 0];
  const ca = [0, 0, 0, 0];
  const c0 = pipe.clicks[0];
  const c1 = pipe.clicks[1];
  if (c0) {
    const age = t - c0.t;
    if (age < CLICK_LIFETIME) {
      cp[0] = c0.x; cp[1] = c0.y; ca[0] = age;
    } else { cp[0] = -1; ca[0] = CLICK_LIFETIME; }
  } else { cp[0] = -1; ca[0] = CLICK_LIFETIME; }
  if (c1) {
    const age = t - c1.t;
    if (age < CLICK_LIFETIME) {
      cp[2] = c1.x; cp[3] = c1.y; ca[1] = age;
    } else { cp[2] = -1; ca[1] = CLICK_LIFETIME; }
  } else { cp[2] = -1; ca[1] = CLICK_LIFETIME; }

  gl.useProgram(pipe.program);
  gl.uniform2f(pipe.uResolution!, width, height);
  gl.uniform1f(pipe.uTime!, t);
  gl.uniform3f(pipe.uBass!, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(pipe.uMid!, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(pipe.uHigh!, pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.uniform2f(pipe.uPointer!, pipe.smoothX, pipe.smoothY);
  gl.uniform1f(pipe.uPointerActive!, pipe._active);
  gl.uniform4f(pipe.uClickPos!, cp[0], cp[1], cp[2], cp[3]);
  gl.uniform4f(pipe.uClickAge!, ca[0], ca[1], ca[2], ca[3]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
